import { NgIf } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	inject,
	OnInit,
	output,
	signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { BusinessPartnerContactPerson } from '@oe-backend/types';
import { HeadlineComponent } from '@zeiss/beyond-online-angular';
import { LoadingAnimationDirective } from 'src/app/modules/loading-animation/loading-animation.directive';
import { IconButtonComponent, IconComponent } from 'vis-ods-angular';
import { RoutesEnum } from '../../../routing/routes.enum';
import { SummaryRoutesEnum } from '../../../routing/summary-outlet-routes.enum';
import { CartStore } from '../../../state-management/stores/cart.store';
import { ECPStore } from '../../../state-management/stores/ecp.store';
import { OrderStore } from '../../../state-management/stores/order.store';

@Component({
	selector: 'app-contact-person-selector',
	imports: [
		TranslocoModule,
		IconButtonComponent,
		HeadlineComponent,
		IconComponent,
		NgIf,
		LoadingAnimationDirective,
	],
	templateUrl: './contact-person-selector.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContactPersonSelectorComponent implements OnInit {
	private readonly router = inject(Router);
	private readonly orderStore = inject(OrderStore);
	private readonly cartStore = inject(CartStore);
	private readonly ecpStore = inject(ECPStore);
	private readonly $searchTerm = signal<string>('');
	private readonly $orderIsCartItem = computed(() => this.orderStore.selectedCartItemId() !== null);

	public readonly contactPersonSelected = output<BusinessPartnerContactPerson>();

	public readonly $contactPersons = computed(() =>
		this.ecpStore
			.shipToContacts()
			.filter(
				(contact) =>
					contact.firstName?.toLowerCase().includes(this.$searchTerm().toLowerCase()) ||
					contact.lastName?.toLowerCase().includes(this.$searchTerm().toLowerCase()),
			),
	);

	public readonly $loading = this.ecpStore.isLoading('shipToContacts');
	public readonly $updatingCartItem = this.cartStore.isLoading('updateCartItem');

	public readonly $selectedContactPerson = computed(
		() => this.orderStore.order().shipTo?.contactPerson,
	);

	ngOnInit() {
		const shipToNumber = this.orderStore.order().shipTo?.customerNumber;
		if (shipToNumber) {
			this.ecpStore.fetchShipToContactPersons(shipToNumber);
		}
	}

	searchTermChanged(searchTerm: string) {
		this.$searchTerm.set(searchTerm);
	}

	async onContactPersonChange(contactPerson: BusinessPartnerContactPerson) {
		this.orderStore.setContactPerson(contactPerson);
		if (this.$orderIsCartItem()) {
			const cartItemId = this.orderStore.selectedCartItemId();
			if (cartItemId) {
				const order = this.orderStore.order();
				await this.cartStore.updateCartItem(cartItemId, order);
				if (this.cartStore.hasError('updateCartItem')()) return;
				this.cartStore.fetchCartItems();
			}
		}
		this.navigateBack();
	}

	navigateBack() {
		if (this.router.url.includes(RoutesEnum.shoppingCart)) {
			this.router.navigate([{ outlets: { summary: [SummaryRoutesEnum.shoppingCartSummary] } }]);
		} else {
			this.router.navigate([{ outlets: { summary: [SummaryRoutesEnum.summary] } }]);
		}
	}
}
